import { z } from 'zod';

// scanner_config row (one per tenant). Returned by config.get + each
// config.* mutation as the new state.

export const ScannerConfigRowSchema = z.object({
  tenantId: z.string().uuid(),
  minEdgePct: z.number(),
  cooldownSec: z.number(),
  cooldownBreakEdgeDeltaPct: z.number(),
  allowedBooks: z.array(z.string()),
  priorityBooks: z.record(z.number().int()),
  bookMetadata: z.record(z.record(z.unknown())),
  prematchMaxStalenessSec: z.number(),
  liveMaxStalenessSec: z.number(),
  weakenedToClosedMin: z.number(),
  minSecondsBeforeMatch: z.number(),
  minConfidence: z.number().nullable(),
  activeModelIds: z.array(z.string()),
  shadowModelIds: z.array(z.string()),
  updatedAt: z.string().datetime(),
});
export type ScannerConfigRow = z.infer<typeof ScannerConfigRowSchema>;

// Patch shape — every field optional so callers send only what they change.
// .refine() rejects empty payloads (a no-op patch is almost certainly a bug).
export const ScannerConfigPatchSchema = z
  .object({
    minEdgePct: z.number().min(0).max(1),
    cooldownSec: z
      .number()
      .int()
      .min(0)
      .max(60 * 60),
    cooldownBreakEdgeDeltaPct: z.number().min(0).max(1),
    allowedBooks: z.array(z.string().min(1)).min(1),
    priorityBooks: z.record(z.number().int().min(1)),
    prematchMaxStalenessSec: z
      .number()
      .int()
      .min(1)
      .max(60 * 60),
    liveMaxStalenessSec: z
      .number()
      .int()
      .min(1)
      .max(60 * 60),
    weakenedToClosedMin: z
      .number()
      .int()
      .min(1)
      .max(24 * 60),
    minSecondsBeforeMatch: z
      .number()
      .int()
      .min(0)
      .max(24 * 60 * 60),
    minConfidence: z.number().min(0).max(1).nullable(),
    activeModelIds: z.array(z.string().min(1)).min(1),
    shadowModelIds: z.array(z.string().min(1)),
  })
  .partial()
  .refine(
    (v) => Object.keys(v).length > 0,
    'config.update must contain at least one field to change',
  );
export type ScannerConfigPatch = z.infer<typeof ScannerConfigPatchSchema>;

export const ScannerConfigAddBookInputSchema = z.object({
  book: z.string().min(1).max(64),
  priority: z.number().int().min(1).max(999),
  // Optional metadata blob — { type: 'sharp' | 'soft', notes?: string, ... }
  metadata: z.record(z.unknown()).optional(),
});
export type ScannerConfigAddBookInput = z.infer<typeof ScannerConfigAddBookInputSchema>;

export const ScannerConfigRemoveBookInputSchema = z.object({
  book: z.string().min(1).max(64),
});
export type ScannerConfigRemoveBookInput = z.infer<typeof ScannerConfigRemoveBookInputSchema>;

export const ScannerConfigSetBookMetadataInputSchema = z.object({
  book: z.string().min(1).max(64),
  metadata: z.record(z.unknown()),
});
export type ScannerConfigSetBookMetadataInput = z.infer<
  typeof ScannerConfigSetBookMetadataInputSchema
>;
